import { Component, Element, Host, Prop, h, Method } from '@stencil/core';
import {
  hasShadowDom,
  injectFont,
  parseAndGetAriaAttributes,
  SelectedAriaAttributes,
} from '../../utils';

import { TIcon } from '../../svg/icons';
import {
  TButtonAlign,
  TButtonAriaAttributes,
  TButtonSize,
  TButtonType,
  TButtonVariant,
} from './button-utils';

@Component({
  tag: 'dc-ui-button',
  styleUrl: 'dc-ui-button.scss',
  shadow: { delegatesFocus: true },
})
export class DcUiButton {
  @Element() host: HTMLElement;
  button: HTMLButtonElement;

  /** Adds a size to the button. Default value is 'normal'.*/
  @Prop() size: TButtonSize = 'normal';
  /** Adds a type to the button. Default value is 'submit'. */
  @Prop() type: TButtonType = 'submit';
  /** Adds a variant to the button. Default value is 'secondary'. */
  @Prop() variant: TButtonVariant = 'secondary';
  /** Adds an icon to the button. Default value is an empty string. */
  @Prop() icon?: TIcon;
  /** Adds custom icon to the button. Using URL */
  @Prop() iconSource?: string;
  /** Hides the label on the button. Default value is false. */
  @Prop() hideLabel = false;
  /** Disables the button on the interface. Default value is false. */
  @Prop() disabled = false;
  /** Adds an alignment to the button. Default value is 'center'. */
  @Prop() align?: TButtonAlign;
  /** (optional) Adds a name to the button. This is useful for form submissions. */
  @Prop() name?: string;
  @Prop() aria?: SelectedAriaAttributes<TButtonAriaAttributes>;

  componentWillLoad() {
    injectFont();
  }

  @Method()
  async focusElement() {
    this.button.focus();
  }

  /**
   * Hack to make the button behave has expected when inside forms.
   * @see https://github.com/ionic-team/ionic-framework/blob/master/core/src/components/button/button.tsx#L155-L175
   */
  handleClick = (ev: Event) => {
    if (hasShadowDom(this.host)) {
      const parentForm = this.host.closest('form');
      if (parentForm) {
        ev.preventDefault();

        const fakeButton = document.createElement('button');
        if (this.type) {
          fakeButton.type = this.type;
        }
        fakeButton.style.display = 'none';
        parentForm.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  };

  render() {
    return (
      <Host
        onClick={(e) => {
          if (this.disabled) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
        }}>
        <button
          class={`
            dc-ui-btn
            dc-ui-btn--${this.size}
            dc-ui-btn--${this.variant}
            ${this.hideLabel && this.icon ? 'dc-ui-btn--hide-label' : ''}
            ${this.disabled ? 'dc-ui-btn--disabled' : ''}
            ${this.align ? `dc-ui-btn--align-${this.align}` : ''}
            ${this.icon ? 'dc-ui-btn--icon' : ''}
          `}
          type={this.type}
          disabled={this.disabled}
          aria-disabled={this.disabled ? 'true' : null}
          {...parseAndGetAriaAttributes(this.aria)}
          name={this.name}
          ref={(button) => (this.button = button)}
          onClick={this.handleClick}>
          {this.icon || this.iconSource ? (
            <dc-ui-icon
              aria-hidden="true"
              name={this.icon}
              source={this.iconSource}
              size={this.size === 'normal' ? 'medium' : this.size}></dc-ui-icon>
          ) : null}
          <span>
            <slot />
          </span>
        </button>
      </Host>
    );
  }
}
